import React, { ChangeEvent, FC, ReactElement, useEffect } from "react";
import AppSearchBox from "./searchBox"
import DualListboxCheckboxItem, { ItemEnableButtonProps } from "./dualListboxCheckboxItem";
import { AdminFormFieldSubmit } from "./admin/formFields";
import { useTranslation } from "react-i18next";
import { TCustomizableItemList } from "./types";
import svgIcons from "../assets/svg/svgIcons";
import { APP_CONFIG } from "../constants/constants";

type ItemsProps = {
  listContainerRef?: React.RefObject<HTMLDivElement>;
  title: string;
  searchKeyword?: string;
  searchPlaceholder: string;
  searchCallback: (e: React.ChangeEvent<HTMLInputElement>) => void;
  actualListOfItems?: TCustomizableItemList[]; // for inactive count
  listOfItems: TCustomizableItemList[];
  selectedItems?: TCustomizableItemList[]; // items that are currently selected
  handleCheckboxChange: ({ item, isChecked }: { item: any, isChecked: boolean }) => void;
  loading?: boolean;
  disabled?: boolean;
  groupPage: boolean;
  showInactiveEntities: boolean;
  showInactiveEntitiesText?: boolean;
  showStatusTagOnItems?: boolean;
  wrapperClassName?: string;
  PaginationComponent?: React.ReactNode; // for displaying pagination
  ItemEnableButton?: (props: ItemEnableButtonProps) => ReactElement<ItemEnableButtonProps> | null;
  renderItemAction?: (item: TCustomizableItemList) => ReactElement | null;
};

const Items: FC<ItemsProps> = ({
  listContainerRef,
  title,
  searchPlaceholder,
  searchKeyword = "",
  searchCallback,
  actualListOfItems = [], // for inactive count
  listOfItems,
  selectedItems = [],
  handleCheckboxChange,
  loading = false,
  disabled,
  groupPage,
  showInactiveEntities,
  showInactiveEntitiesText = false,
  showStatusTagOnItems = false,
  wrapperClassName = "col-span-4",
  PaginationComponent = null,
  ItemEnableButton,
  renderItemAction
}) => {

  const { t } = useTranslation('translation', { keyPrefix: 'dualListbox' });

  return (
    <div className={wrapperClassName}>
      <label
        className={`flex justify-between mb-2 text-sm font-display font-semibold text-field-label-valid`}
      >
        {title}
        {showInactiveEntitiesText && (
          <span className="flex gap-2">
            <span className="text-sm text-accent-green">{" "}{t("active")}{":"} {(actualListOfItems?.filter((item) => item.isactive === 1)).length}</span>
            <span className="text-sm text-field-error-border"> {" "}{t("inactive")}{":"} {(actualListOfItems?.filter((item) => item.isactive !== 1)).length}</span>
          </span>
        )}
      </label>
      <AppSearchBox
        placeholder={searchPlaceholder}
        onChange={searchCallback}
        onClear={() => {
          searchCallback({ target: { value: "" } } as ChangeEvent<HTMLInputElement>)
        }}
      />
      <div className="h-96 mt-4 flex flex-col border border-gray-500 rounded-lg relative">
        {/* <fieldset>
          <legend className="sr-only">Checkboxes</legend> */}
        <div
          ref={listContainerRef}
          className="flex-grow flex flex-col gap-2 p-2 overflow-y-auto"
        >
          {PaginationComponent !== null && (
            <>
              {listOfItems?.length === 0 && searchKeyword === '' && (
                <p className="flex-grow flex flex-col gap-1 items-center justify-center text-xs text-gray-400 text-center mb-2 mx-2">
                  {/* <span>{t("all_page_items_added")}{" "}{t("all_page_items_added_line2")}</span> */}
                  <span>{t(loading ? "items_loading" : "all_items_added")}</span>
                </p>
              )}
            </>
          )}
          {listOfItems?.map((item: any) => {
            return (
              <DualListboxCheckboxItem
                key={item.id}
                id={item.id}
                title={item.title || item.id}
                sub_title={item.sub_title || "-"}
                defaultChecked={selectedItems.some((selectedItem) => selectedItem.id === item.id)}
                handleChange={(e) => handleCheckboxChange({ item: item, isChecked: e.target.checked })}
                show={groupPage ? (showInactiveEntities ? true : item.isactive === 1) : true}
                disabled={disabled}
                {...(!!showStatusTagOnItems && { statusTag: item.isactive === 1 })}
                metaData={item.metaData}
                ItemAction={renderItemAction?.(item) ?? null}
                NestedEnableButton={
                  !!ItemEnableButton
                    ? (
                      (props) => (
                        <ItemEnableButton
                          {...props}
                          disabled={
                            disabled ||
                            (showInactiveEntities
                              ? false
                              : item.isactive !== 1
                            )
                          }
                        />
                      )
                    )
                    : undefined
                }
              />
            )
          })}
        </div>
        {/* </fieldset> */}
        {PaginationComponent}
      </div>
    </div>
  )
}

type MoveButtons = 1 | 2 | 3 | 4;

type DualListboxProps = {
  leftListContainerRef?: React.RefObject<HTMLDivElement>;
  leftLoading?: boolean;
  leftTitle: string;
  leftSearchKeyword?: string;
  leftSearchPlaceholder: string;
  leftSearchCallback?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  leftList: TCustomizableItemList[];
  leftPaginationComponentClassName?: string;
  LeftPaginationComponent?: React.ReactNode; // if provided, activates pagination mode
  leftFilterItemsBasedOnRightList?: boolean;
  LeftItemEnableButton?: (props: ItemEnableButtonProps) => ReactElement | null;
  renderItemAction?: (item: TCustomizableItemList) => ReactElement | null; // both sides
  disabled?: boolean;
  rightTitle: string;
  rightSearchPlaceholder: string;
  rightSearchCallback?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  initialRightList: TCustomizableItemList[];
  rightListItems: TCustomizableItemList[];
  setRightListItems: (items: TCustomizableItemList[]) => void;
  handleCancelButton: () => void;
  handleSaveButton: (leftListItems: TCustomizableItemList[]) => void;
  groupPage?: boolean;
  showInactiveEntities?: boolean;
  showInactiveEntitiesText?: boolean;
  saveButtonText?: string;
  showStatusTagOnItems?: boolean;
  displayRightSideOnly?: boolean; // used when viewing only ACL is applicable
  isAddAllDisabled?: boolean;
  moveButtons?: MoveButtons[];
  hideActionButtons?: boolean; // hide the internal Save/Cancel buttons so a shared external Save/Cancel can be used
};


export const DualListboxPagedLHS: FC<DualListboxProps> = ({
  leftListContainerRef,
  leftLoading = false,
  leftTitle,
  leftSearchKeyword = "",
  leftSearchPlaceholder,
  leftSearchCallback,
  leftList,
  leftPaginationComponentClassName = "bg-gray-100 text-xs p-2 rounded-bl-lg rounded-br-lg border-t border-black/10",
  LeftPaginationComponent = null, // if provided, activates pagination mode
  leftFilterItemsBasedOnRightList = false,
  LeftItemEnableButton,
  renderItemAction,
  disabled,
  rightTitle,
  rightSearchCallback,
  rightSearchPlaceholder,
  initialRightList,
  rightListItems,
  setRightListItems,
  handleCancelButton,
  handleSaveButton,
  groupPage = false,
  showInactiveEntities = false,
  showInactiveEntitiesText = false,
  saveButtonText = "Save",
  showStatusTagOnItems = false,
  displayRightSideOnly = false,
  isAddAllDisabled = false,
  moveButtons = [1, 2, 3, 4],
  hideActionButtons = false
}) => {

  const isPaginationMode = typeof LeftPaginationComponent !== 'undefined';

  const { t: tMain } = useTranslation();
  const { t } = useTranslation('translation', { keyPrefix: 'dualListbox' });

  // current list mechanism
  const [leftListItems, setLeftListItems] = React.useState<TCustomizableItemList[]>(leftList);
  const [filteredLeftListItems, setFilteredLeftListItems] = React.useState<TCustomizableItemList[]>(
    leftList
      .filter((item) => !leftFilterItemsBasedOnRightList ? true : rightListItems.every((currentItem) => currentItem.id !== item.id))
      .filter((item) => showInactiveEntities ? true : item.isactive === 1)
  );
  // const [rightListItems, setRightListItems] = React.useState<TCustomizableItemList[]>(rightList);
  const [filteredRightListItems, setFilteredRightListItems] = React.useState<TCustomizableItemList[]>(
    rightListItems
      .filter((item) => showInactiveEntities ? true : item.isactive === 1)
  );

  // selected mechanism
  const [leftSelectedItems, setLeftSelectedItems] = React.useState<TCustomizableItemList[]>([]);
  const [rightSelectedItems, setRightSelectedItems] = React.useState<TCustomizableItemList[]>([]);

  // const [editStart, setEditStart] = React.useState(false); // To check if user has moved items from left to right or vice versa

  // React.useEffect(() => {
  //   // if (!editStart) { // Do not update list if user has moved items from left to right or vice versa
  //   setRightListItems(rightList);
  //   // }
  // }, [rightList]) //, editStart])

  // update leftListItems when leftList changes
  React.useEffect(() => {
    setLeftListItems(leftList);
  }, [leftList])

  React.useEffect(() => {
    // if (!editStart) { // Do not update list if user has moved items from left to right or vice versa
    // setLeftListItems(leftList.filter((item) => !rightListItems.some((currentItem) => currentItem.id === item.id)));
    setFilteredLeftListItems(
      leftList
        .filter((item) => !leftFilterItemsBasedOnRightList ? true : rightListItems.every((currentItem) => currentItem.id !== item.id))
        .filter((item) => showInactiveEntities ? true : item.isactive === 1)
    );
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftList, rightListItems, showInactiveEntities]) //, editStart])


  // handle left checkbox selection
  const handleLeftCheckboxChange = ({ item, isChecked }: { item: any; isChecked: boolean }) => {
    if (isChecked && !leftSelectedItems.find((selected)=>selected.id===item.id)) {
      setLeftSelectedItems([...leftSelectedItems, item]);
    } else {
      setLeftSelectedItems(leftSelectedItems.filter((currentItem) => currentItem.id !== item.id));
    }
  }

  // handle right checkbox selection
  const handleRightCheckboxChange = ({ item, isChecked }: { item: any; isChecked: boolean }) => {
    if (isChecked && !rightSelectedItems.find((selected)=>selected.id===item.id)) {
      setRightSelectedItems([...rightSelectedItems, item]);
    } else {
      setRightSelectedItems(rightSelectedItems.filter((currentItem) => currentItem.id !== item.id));
    }
  }

  // handle add all vehicles from all vehicles
  const handleAddAll = () => {
    // if (!isPaginationMode) setEditStart(true); // User has moved items from left to right
    setRightListItems([...rightListItems, ...filteredLeftListItems]);
    // setFilteredRightListItems([...filteredRightListItems, ...leftListItems]);
    // setFilteredLeftListItems([]);
    setLeftSelectedItems([]);
  }

  // handle add selected items from all items
  const handleAddSelected = () => {
    // if (!isPaginationMode) setEditStart(true); // User has moved items from left to right
    setRightListItems([...rightListItems, ...leftSelectedItems]);
    // setFilteredRightListItems([...filteredRightListItems, ...leftSelectedItems]);
    // setFilteredLeftListItems(leftListItems.filter((item) => !leftSelectedItems.some((currentItem) => currentItem.id === item.id)));
    setLeftSelectedItems([]);
  }

  // handle remove selected items from group items
  const handleRemoveSelected = () => {
    // if (!isPaginationMode) setEditStart(true); // User has moved items from right to left
    // setFilteredLeftListItems([...leftListItems, ...rightSelectedItems]);
    setRightListItems(rightListItems.filter((item) => !rightSelectedItems.some((currentItem) => currentItem.id === item.id)));
    // setFilteredRightListItems(filteredRightListItems.filter((item) => !rightSelectedItems.some((currentItem) => currentItem.id === item.id)));
    setRightSelectedItems([]);
  }

  // handle remove all vehicles from group vehicles
  const handleRemoveAll = () => {
    // if (!isPaginationMode) setEditStart(true); // User has moved items from right to left
    // setFilteredLeftListItems([...leftListItems, ...rightListItems]);
    setRightListItems([]);
    setRightSelectedItems([]);
  }

  // update filteredRightListItems when rightListItems changes or when showInactiveEntities changes
  useEffect(() => {
    setFilteredRightListItems(
      rightListItems
        .filter((item) => showInactiveEntities ? true : item.isactive === 1)
    );
  }, [rightListItems, showInactiveEntities])

  // update filteredLeftListItems when leftListItems changes or when rightListItems changes or when showInactiveEntities changes
  useEffect(() => {
    setFilteredLeftListItems(
      leftList
        .filter((item) => !leftFilterItemsBasedOnRightList ? true : rightListItems.every((currentItem) => currentItem.id !== item.id))
        .filter((item) => showInactiveEntities ? true : item.isactive === 1)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftList, rightListItems, showInactiveEntities])

  // handle cancel button
  const handleCancel = () => {
    setRightListItems(initialRightList);
    setFilteredRightListItems(initialRightList);
    handleCancelButton();
  }

  const onRightSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFilteredRightListItems(
      rightListItems
        .filter((item) =>
          item.title
            .toLowerCase()
            .includes(e.target.value.toLowerCase()) ||
          // allow searching but sub_title
          item.sub_title
            ?.toLowerCase()
            .includes(e.target.value.toLowerCase())
        )
    );
  }

  return (
    <>
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <div className="fixed top-0 left-0 flex p-2 bg-gray-200 text-[8px] font-bold opacity-80 z-map-popup group-[]/ftmodal:-z-10 h-screen overflow-auto">
          <pre className="">
            <span className="text-xs text-accent-green">leftListItems ({leftListItems.length}): </span>
            {JSON.stringify(leftListItems, null, 2)}<br /><br />
            <span className="text-xs text-accent-green">filteredLeftListItems ({filteredLeftListItems.length}): </span>
            {JSON.stringify(filteredLeftListItems, null, 2)}<br /><br />
          </pre>
          <pre className="">
            <span className="text-xs text-accent-green">rightListItems ({rightListItems.length}): </span>
            {JSON.stringify(rightListItems, null, 2)}<br /><br />
            <span className="text-xs text-accent-green">filteredRightListItems ({filteredRightListItems.length}): </span>
            {JSON.stringify(filteredRightListItems, null, 2)}<br /><br />
          </pre>
        </div>
      )}
      {/* <div className="grid grid-cols-12 gap-4"> */}

      {/* ---- LEFT SIDE ---- */}
      {!displayRightSideOnly && (
        <Items
          title={leftTitle}
          searchKeyword={leftSearchKeyword}
          searchPlaceholder={leftSearchPlaceholder}
          searchCallback={leftSearchCallback ? leftSearchCallback : (e) => { console.log('left search') }}
          listOfItems={
            filteredLeftListItems
              .filter((item) => true
                // filteredRightListItems
                //   .every((currentItem) => currentItem.id !== item.id)

                // isPaginationMode
                //   // if pagination mode, check if item is not in the right list
                //   ? (!!rightSearchCallback ? rightListItems : filteredRightListItems)
                //     .every((currentItem) => currentItem.id !== item.id && currentItem.id !== item.id)
                //   // else no filtration
                //   : true
              )
          }
          selectedItems={leftSelectedItems}
          listContainerRef={leftListContainerRef}
          handleCheckboxChange={handleLeftCheckboxChange}
          disabled={disabled || leftLoading}
          loading={leftLoading}
          groupPage={groupPage}
          showInactiveEntities={showInactiveEntities}
          showStatusTagOnItems={showStatusTagOnItems}
          PaginationComponent={
            !!LeftPaginationComponent
              ? (
                <div className={leftPaginationComponentClassName}>
                  {LeftPaginationComponent}
                </div>
              )
              : null
          }
          ItemEnableButton={LeftItemEnableButton}
          renderItemAction={renderItemAction}
        />
      )}

      {/* ---- BUTTONS ---- */}
      {!displayRightSideOnly && (
        <div className={`flex flex-col space-y-2 col-span-4`}>
          <div className="block h-20"></div>
          {moveButtons.includes(1) && !isAddAllDisabled &&(
            <div className="relative">
              <AdminFormFieldSubmit
                type="button"
                variant="primary-like"
                label={
                  <>
                    <span className="hidden xl:block mr-2">{t(isPaginationMode ? "add_all_on_this_page" : "add_all")}</span>
                    <img
                      src={svgIcons.FastForwardIcon}
                      alt="fast-forward-icon"
                      className="size-5"
                    />
                  </>}
                titleText={t("add_all")}
                onClick={handleAddAll}
                disabled={disabled || filteredLeftListItems.length === 0}
              />
            </div>
          )}
          {moveButtons.includes(2) && (
            <div className="relative">
              <AdminFormFieldSubmit
                type="button"
                variant="secondary"
                label={
                  <>
                    <span className="hidden xl:block mr-2">{t("add_selected")}</span>
                    <img
                      src={svgIcons.ForwardIcon}
                      alt="fast-forward-icon"
                      className="size-5"
                    />
                  </>}
                titleText={t("add_selected")}
                onClick={handleAddSelected}
                disabled={disabled || leftSelectedItems.length === 0}
              />
            </div>
          )}
          {moveButtons.includes(3) && (
            <div className="relative">
              <AdminFormFieldSubmit
                type="button"
                variant="secondary"
                label={
                  <>
                    <img
                      src={svgIcons.BackwardIcon}
                      alt="fast-forward-icon"
                      className="size-5"
                    />
                    <span className="hidden xl:block ml-2">{t("remove_selected")}</span>
                  </>}
                titleText={t("remove_selected")}
                onClick={handleRemoveSelected}
                disabled={disabled || rightSelectedItems.length === 0}
              />
            </div>
          )}
          {moveButtons.includes(4) && (
            <div className="relative">
              <AdminFormFieldSubmit
                type="button"
                variant="primary-like"
                label={
                  <>
                    <img
                      src={svgIcons.FastBackwardIcon}
                      alt="fast-forward-icon"
                      className="size-5"
                    />
                    <span className="hidden xl:block ml-2">{t("remove_all")}</span>
                  </>}
                titleText={t("remove_all")}
                onClick={handleRemoveAll || rightListItems.length === 0}
                disabled={disabled || rightListItems.length === 0}
              />
            </div>
          )}
          <div className="flex-grow"></div>
          {!hideActionButtons && (
            <>
              <AdminFormFieldSubmit
                type="button"
                variant="primary"
                label={tMain("cancel")}
                onClick={handleCancel}
                disabled={disabled}
              />
              <AdminFormFieldSubmit
                type="button"
                variant="success"
                label={saveButtonText}
                onClick={() => {
                  handleSaveButton(
                    (isPaginationMode
                      ? filteredRightListItems
                      : rightListItems)
                      .filter((item) => !!item.id)
                  )
                }}
                disabled={disabled}
              />
            </>
          )}
        </div>
      )}

      {/* ---- RIGHT SIDE ---- */}

      <Items
        title={rightTitle}
        searchPlaceholder={rightSearchPlaceholder}
        searchCallback={rightSearchCallback ? rightSearchCallback : onRightSearchChange}
        actualListOfItems={rightListItems}
        listOfItems={rightSearchCallback ? rightListItems : filteredRightListItems}
        selectedItems={rightSelectedItems}
        handleCheckboxChange={handleRightCheckboxChange}
        disabled={disabled}
        groupPage={groupPage}
        showInactiveEntities={showInactiveEntities}
        showInactiveEntitiesText={showInactiveEntitiesText}
        showStatusTagOnItems={showStatusTagOnItems}
        wrapperClassName={displayRightSideOnly ? "col-span-12" : "col-span-4"}
        renderItemAction={renderItemAction}
      />
      {/* </div > */}
    </>
  )
}

export default DualListboxPagedLHS;